import React, { useEffect } from 'react'
import { View } from 'react-native'

import MentionsModal from './MentionsModal'
import Popover from 'react-tiny-popover'
import { useDispatch, useSelector } from 'react-redux'
import { hideFloatPopup, showFloatPopup } from '../../../redux/actions'
import {
    COMMENT_MODAL_ID,
    exitsOpenModals,
    FOLLOW_UP_MODAL_ID,
    MANAGE_TASK_MODAL_ID,
    MENTION_MODAL_ID,
    TAGS_INTERACTION_MODAL_ID,
    TASK_DESCRIPTION_MODAL_ID,
    WORKFLOW_MODAL_ID,
} from '../../ModalsManager/modalsManager'
import useWindowSize from '../../../utils/useWindowSize'
import { MENTION_MODAL_MIN_HEIGHT, popoverToCenter } from '../../../utils/HelperFunctions'
import { computeSafeAreaModalMaxHeightBelow, getSafeAreaModalMaxHeightBelow } from '../../../utils/modalSafeArea'
import { getSafeAreaInsets } from '../../../utils/safeAreaInsets'
import { getWorkspaceInsets } from '../../../utils/workspaceViewport'
import { MODAL_Z_AUTOCOMPLETE } from '../../styles/modals'

// AT-2397. The mention list is portaled to `document.body`, so it is a SIBLING
// of whatever popup hosts the input, not a descendant of it — being nested in
// the React tree buys it nothing in the stacking order. Without a z-index of
// its own the vendored container is left at `z-index: auto` and loses to any
// host that sets one, which is why typing "@" inside the "Add task" popup
// (`zIndex: 9999`) opened the list *behind* the card: positioned, painted,
// clickable in principle, and completely invisible.
//
// Kept module-level because react-tiny-popover applies `containerStyle` once,
// when it creates the portal container — a fresh object literal per render
// would be silently ignored after mount anyway, and a stable reference makes
// that explicit.
const MENTIONS_POPOVER_CONTAINER_STYLE = { zIndex: MODAL_Z_AUTOCOMPLETE }

export default function WrapperMentionsModal({
    mentionText,
    selectItemToMention,
    projectId,
    contentLocation,
    setMentionModalHeight,
    keepFocus,
    inMentionsEditionTag,
    insertNormalMention,
    getMentionViewport,
}) {
    const [width, height] = useWindowSize()
    // Anna's chat is beside the app viewport. Its autocomplete must use the
    // conversation's bounds rather than being pushed into the Alldone pane.
    const viewport = getMentionViewport?.()
    const maxHeight = viewport
        ? computeSafeAreaModalMaxHeightBelow({
              windowHeight: viewport.height,
              topOffset: contentLocation.top - viewport.top,
              insets: getWorkspaceInsets(getSafeAreaInsets(), viewport),
          })
        : getSafeAreaModalMaxHeightBelow(height, contentLocation.top)
    const finalLocation = maxHeight < MENTION_MODAL_MIN_HEIGHT ? null : contentLocation
    const mobile = useSelector(state => state.smallScreenNavigation)
    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(showFloatPopup())
        return () => dispatch(hideFloatPopup())
    }, [])

    const onClickOutside = () => {
        if (
            !exitsOpenModals([
                MENTION_MODAL_ID,
                COMMENT_MODAL_ID,
                MANAGE_TASK_MODAL_ID,
                FOLLOW_UP_MODAL_ID,
                WORKFLOW_MODAL_ID,
                TASK_DESCRIPTION_MODAL_ID,
                TAGS_INTERACTION_MODAL_ID,
            ])
        ) {
            insertNormalMention()
        }
    }

    return (
        <Popover
            content={
                <MentionsModal
                    mentionText={mentionText}
                    selectItemToMention={selectItemToMention}
                    projectId={projectId}
                    setMentionModalHeight={setMentionModalHeight}
                    keepFocus={keepFocus}
                    inMentionsEditionTag={inMentionsEditionTag}
                    insertNormalMention={insertNormalMention}
                    contentLocation={finalLocation}
                    viewport={viewport}
                />
            }
            isOpen={true}
            position={['top', 'right', 'bottom', 'left']}
            padding={4}
            align={'start'}
            containerStyle={MENTIONS_POPOVER_CONTAINER_STYLE}
            viewport={viewport}
            onClickOutside={onClickOutside}
            contentLocation={
                finalLocation
                    ? finalLocation
                    : viewport
                      ? ({ popoverRect }) => ({
                            top: viewport.top + Math.max(16, (viewport.height - popoverRect.height) / 2),
                            left: viewport.left + Math.max(16, (viewport.width - popoverRect.width) / 2),
                        })
                      : args => popoverToCenter(args, mobile)
            }
        >
            <View onClick={e => e.stopPropagation()} />
        </Popover>
    )
}
